/**
 * Reveal staging for forge results (crafting.md §7.1–§7.2), as pure data + a time-driven state
 * machine (DOM-free, unit-tested in revealModel.test.ts). The DOM layer (forgeReveal.ts) only
 * asks "what phase / colour / shake is it at t ms?" and fires the cues the machine emits.
 *
 * Honesty rules baked in here:
 *  - the colour climb only goes UP TO the true result's colour (no fake gold flashes); a tier
 *    piece adds a final teal "set" step because it really is a set piece;
 *  - the roll needle sweeps to the real roll, so a near miss is seen as it happened.
 *
 * Craft timings (total / anticipation / climb steps / hit / hold):
 *   common 0.5 s · 150 · 0 · 80 · rest     uncommon 0.8 s · 250 · 1 · 100 · rest
 *   rare 1.2 s · 300 · 2 · 150 hit-stop     epic 2.0 s · 400 + altar shake · 3 · 150 + 4 px · 600 hold
 *   legendary / tier 3.0 s · 500 · 4 · 200 + 8 px + particles · 1000 hold
 * Repeated Commons / Uncommons (already seen this session) shorten to 0.3 s; reduced motion
 * halves every timing and drops the shake.
 */
import type { Rarity } from '@keystone/progression';

export type RevealTier = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export type RevealPhase = 'anticipation' | 'climb' | 'nearMiss' | 'hit' | 'hold' | 'done';

/** Colour steps of the climb, lowest first (the teal step only for tier pieces). */
export type ClimbColor = Rarity | 'set';

export const CLIMB_ORDER: readonly Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

export interface RevealCue {
  at: number;
  /** Sound / juice cue id (e.g. 'sfx.reveal.epic'); the DOM maps these to effects. */
  id: string;
}

export interface RevealPlan {
  rarity: Rarity;
  tierPiece: boolean;
  nearMiss: boolean;
  /** Short repeat (a Common / Uncommon already seen this session). */
  short: boolean;
  reducedMotion: boolean;
  anticipation: number;
  /** Colours climbed through after the starting grey (the last one is the result colour). */
  climb: ClimbColor[];
  stepMs: number;
  /** Extra beat where the roll needle stalls just short of the next band (Epic near misses). */
  nearMissMs: number;
  hit: number;
  hold: number;
  total: number;
  /** Screen shake on the hit, px (0 = none). */
  shake: number;
  /** Particle burst size (0 = none). */
  particles: number;
  /** Altar shakes during anticipation (Epic+). */
  altarShake: boolean;
  /** Banner on the card ("2/4 SET BONUS ACTIVE"), or null. */
  banner: string | null;
  cues: RevealCue[];
}

export interface RevealInput {
  rarity: Rarity;
  tierPiece?: boolean;
  nearMiss?: boolean;
  /** This rarity was already revealed this session (repeats of Common / Uncommon shorten). */
  seen?: boolean;
  reducedMotion?: boolean;
  /** A set bonus threshold the new piece crosses when equipped (2 or 4), or null. */
  setCrossed?: number | null;
}

interface Staging {
  total: number;
  anticipation: number;
  steps: number;
  hit: number;
  hold: number;
  shake: number;
  particles: number;
}

const STAGING: Readonly<Record<RevealTier, Staging>> = {
  common: { total: 500, anticipation: 150, steps: 0, hit: 80, hold: 270, shake: 0, particles: 0 },
  uncommon: { total: 800, anticipation: 250, steps: 1, hit: 100, hold: 200, shake: 0, particles: 10 },
  rare: { total: 1200, anticipation: 300, steps: 2, hit: 150, hold: 190, shake: 0, particles: 18 },
  epic: { total: 2000, anticipation: 400, steps: 3, hit: 150, hold: 600, shake: 4, particles: 28 },
  legendary: { total: 3000, anticipation: 500, steps: 4, hit: 200, hold: 1000, shake: 8, particles: 40 },
};

const NEAR_MISS_MS = 360;
const SHORT_TOTAL = 300;

/** Which staging a result uses (a tier piece is staged like a Legendary). */
export function revealTier(rarity: Rarity, tierPiece: boolean): RevealTier {
  return tierPiece ? 'legendary' : rarity;
}

/** Build the plan for one craft reveal. */
export function revealPlan(input: RevealInput): RevealPlan {
  const tierPiece = !!input.tierPiece;
  const nearMiss = !!input.nearMiss && input.rarity !== 'epic' && input.rarity !== 'legendary';
  const tier = revealTier(input.rarity, tierPiece);
  const st = STAGING[tier];
  const rm = !!input.reducedMotion;
  const short = !tierPiece && !nearMiss && !!input.seen && (input.rarity === 'common' || input.rarity === 'uncommon');
  // Climb: from grey up to the true colour (never past it), then teal for a tier piece.
  const top = CLIMB_ORDER.indexOf(input.rarity);
  const climb: ClimbColor[] = CLIMB_ORDER.slice(1, top + 1);
  if (tierPiece) climb.push('set');
  const scale = rm ? 0.5 : 1;
  let anticipation = st.anticipation;
  let hit = st.hit;
  let hold = st.hold;
  let nearMissMs = nearMiss ? NEAR_MISS_MS : 0;
  // The climb fills what the staging table leaves between anticipation, hit and hold.
  let climbMs = Math.max(0, st.total - st.anticipation - st.hit - st.hold);
  if (short) {
    anticipation = 90;
    climbMs = climb.length ? 60 : 0;
    hit = 60;
    hold = SHORT_TOTAL - anticipation - climbMs - hit;
    nearMissMs = 0;
  }
  anticipation = Math.round(anticipation * scale);
  climbMs = Math.round(climbMs * scale);
  hit = Math.round(hit * scale);
  hold = Math.round(hold * scale);
  nearMissMs = Math.round(nearMissMs * scale);
  const stepMs = climb.length ? Math.round(climbMs / climb.length) : 0;
  climbMs = stepMs * climb.length;
  const total = anticipation + climbMs + nearMissMs + hit + hold;
  const shake = rm || short ? 0 : st.shake;
  const particles = short ? 0 : st.particles;
  const banner = tierPiece && input.setCrossed ? `${input.setCrossed}-PIECE BONUS ACTIVE` : null;

  const cues: RevealCue[] = [{ at: 0, id: 'sfx.forge.tap' }];
  if (tier === 'rare') cues.push({ at: 0, id: 'sfx.forge.build' });
  if (tier === 'epic') cues.push({ at: 0, id: 'sfx.forge.build_long' });
  if (tier === 'legendary') cues.push({ at: 0, id: input.rarity === 'legendary' ? 'sfx.forge.eye' : 'sfx.forge.sigil' });
  climb.forEach((c, i) => cues.push({ at: anticipation + i * stepMs, id: `climb.${c}` }));
  if (nearMissMs) cues.push({ at: anticipation + climbMs, id: 'sfx.forge.nearmiss' });
  const hitAt = anticipation + climbMs + nearMissMs;
  cues.push({ at: hitAt, id: `sfx.reveal.${input.rarity}` });
  if (tierPiece) cues.push({ at: hitAt, id: 'sfx.set.piece' });
  if (banner) cues.push({ at: hitAt + hit, id: 'sfx.set.bonus' });
  cues.push({ at: total, id: 'reveal.done' });
  return { rarity: input.rarity, tierPiece, nearMiss, short, reducedMotion: rm, anticipation, climb, stepMs, nearMissMs, hit, hold, total, shake, particles, altarShake: !rm && !short && (tier === 'epic' || tier === 'legendary'), banner, cues };
}

export interface RevealFrame {
  phase: RevealPhase;
  /** Colour shown right now (starts grey: 'common'). */
  color: ClimbColor;
  /** Index into plan.climb of the current step (−1 before the climb). */
  step: number;
  /** 0..1 progress within the current phase. */
  progress: number;
  /** 0..1 of the whole reveal (drives the roll needle sweep). */
  overall: number;
  /** Shake offset amplitude this frame, px. */
  shake: number;
  /** The card is face up. */
  cardShown: boolean;
}

/** Pure frame at time t (ms since the reveal started). */
export function frameAt(plan: RevealPlan, t: number): RevealFrame {
  const climbEnd = plan.anticipation + plan.stepMs * plan.climb.length;
  const nearEnd = climbEnd + plan.nearMissMs;
  const hitEnd = nearEnd + plan.hit;
  const final: ClimbColor = plan.climb[plan.climb.length - 1] ?? 'common';
  const overall = plan.total > 0 ? Math.max(0, Math.min(1, t / plan.total)) : 1;
  if (t >= plan.total) return { phase: 'done', color: final, step: plan.climb.length - 1, progress: 1, overall: 1, shake: 0, cardShown: true };
  if (t < plan.anticipation) {
    const progress = plan.anticipation ? t / plan.anticipation : 1;
    return { phase: 'anticipation', color: 'common', step: -1, progress, overall, shake: plan.altarShake ? 1 + progress : 0, cardShown: false };
  }
  if (t < climbEnd) {
    const k = t - plan.anticipation;
    const step = Math.min(plan.climb.length - 1, Math.floor(k / plan.stepMs));
    return { phase: 'climb', color: plan.climb[step]!, step, progress: (k - step * plan.stepMs) / plan.stepMs, overall, shake: plan.altarShake ? 2 : 0, cardShown: false };
  }
  if (t < nearEnd) return { phase: 'nearMiss', color: final, step: plan.climb.length - 1, progress: (t - climbEnd) / plan.nearMissMs, overall, shake: 0, cardShown: false };
  if (t < hitEnd) {
    const progress = (t - nearEnd) / Math.max(1, plan.hit);
    return { phase: 'hit', color: final, step: plan.climb.length - 1, progress, overall, shake: plan.shake * (1 - progress), cardShown: true };
  }
  return { phase: 'hold', color: final, step: plan.climb.length - 1, progress: (t - hitEnd) / Math.max(1, plan.hold), overall, shake: 0, cardShown: true };
}

/**
 * Drives a plan with injected time. `tick(now)` returns the frame and the cues that fired since
 * the last tick (each cue fires once). `skip()` jumps to the card (the hit cues still fire once,
 * so a skipped Epic still sounds Epic). Click / Space call skip.
 */
export class RevealSequencer {
  private start: number | null = null;
  private fired = 0;
  private skipped = false;
  private lastT = 0;

  constructor(readonly plan: RevealPlan) {}

  /** Advance to `now` (ms, any monotonic clock). The first call starts the reveal. */
  tick(now: number): { frame: RevealFrame; cues: RevealCue[] } {
    if (this.start === null) this.start = now;
    const t = this.skipped ? this.plan.total : now - this.start;
    this.lastT = t;
    const cues: RevealCue[] = [];
    while (this.fired < this.plan.cues.length && this.plan.cues[this.fired]!.at <= t) {
      const c = this.plan.cues[this.fired++]!;
      // A skip drops the climb / build-up cues and keeps the result ones.
      if (this.skipped && (c.id.startsWith('climb.') || c.id.startsWith('sfx.forge.'))) continue;
      cues.push(c);
    }
    return { frame: frameAt(this.plan, t), cues };
  }

  /** Jump to the end (the card). Returns false when already done. */
  skip(): boolean {
    if (this.done) return false;
    this.skipped = true;
    return true;
  }

  get done(): boolean {
    return this.skipped || (this.start !== null && this.lastT >= this.plan.total);
  }

  get wasSkipped(): boolean {
    return this.skipped;
  }
}
